import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Calendar } from '../../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../components/ui/use-toast';
import { CalendarIcon, UserPlus, Clock } from 'lucide-react';
import dayAwayService from '../../services/dayAwayService';
import { useAuth } from '../../context/AuthContext';
import { employeeNamesService } from '../../services/employeeNames';
import '../../styles/calendar-dark-mode-fix.css';

const motivationalMessages = [
  "Rest is not a reward, it's a necessity for peak performance and creativity! ",
  "Taking time to recharge isn't just good for you—it's great for the whole team! ",
  "Balance in work and life leads to better outcomes for everyone! ",
  "Quality rest today leads to quality work tomorrow! ",
  "A well-rested team is a creative and productive team! ",
];

const MotivationalMessage: React.FC = () => {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % motivationalMessages.length);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-2 rounded-lg mb-6 text-center">
      <p className="text-cyan-700 dark:text-cyan-300 font-medium">{motivationalMessages[messageIndex]}</p>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { toast } = useToast();
  const { userEmail } = useAuth();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [note, setNote] = useState('');
  const [names, setNames] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState<{ lastSync: Date | null }>({ lastSync: null });

  useEffect(() => {
    const loadNames = async () => {
      try {
        // Fetch both employee names and sync status in parallel
        const [employeeNames, status] = await Promise.all([
          dayAwayService.getEmployeeNames(),
          dayAwayService.getSyncStatus()
        ]);
        setNames(employeeNames);
        setSyncStatus(status);
        
        // Auto-select current user's name if available
        if (userEmail) {
          const currentUserName = await employeeNamesService.getNameFromEmail(userEmail);
          
          if (currentUserName) {
            // Find exact match
            const exactMatch = employeeNames.find(name => name === currentUserName);
            
            // Try case-insensitive and trimmed match
            const trimmedMatch = employeeNames.find(name =>
              name.trim().toLowerCase() === currentUserName.trim().toLowerCase()
            );
            
            // Try match without punctuation (remove periods, commas, etc.)
            const punctuationMatch = employeeNames.find(name =>
              name.replace(/[.,!?;:]/g, '').trim().toLowerCase() ===
              currentUserName.replace(/[.,!?;:]/g, '').trim().toLowerCase()
            );
            
            // Enhanced partial name matching - handle first name + last initial patterns
            let partialMatch;
            if (!exactMatch && !trimmedMatch && !punctuationMatch) {
              const extractFirstAndLast = (fullName: string): { first: string; last: string } => {
                const parts = fullName.replace(/[.,]/g, '').trim().split(/\s+/).filter(p => p.length > 0);
                return {
                  first: parts[0]?.toLowerCase() || '',
                  last: parts[parts.length - 1]?.toLowerCase() || ''
                };
              };

              const emailNameParts = extractFirstAndLast(currentUserName);
              partialMatch = employeeNames.find(employeeName => {
                const employeeNameParts = extractFirstAndLast(employeeName);
                return emailNameParts.first === employeeNameParts.first &&
                       emailNameParts.last === employeeNameParts.last;
              });
            }
            
            // Use the best match available
            const nameToSet = exactMatch || trimmedMatch || punctuationMatch || partialMatch;
            if (nameToSet) {
              setName(nameToSet);
            }
          }
        }
      } catch (error) {
        console.error('Error loading employee names:', error);
        toast({
          title: "Error",
          description: "Failed to load employee names. Please try refreshing the page.",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    loadNames();
  }, [toast, userEmail]);

  const resetForm = async () => {
    setDate(new Date());
    setType('');
    setNote('');
    
    // Re-select current user's name after reset using the same matching logic
    if (userEmail) {
      try {
        const currentUserName = await employeeNamesService.getNameFromEmail(userEmail);
        if (currentUserName) {
          // Use the same matching logic as in useEffect
          const exactMatch = names.find(name => name === currentUserName);
          const trimmedMatch = names.find(name =>
            name.trim().toLowerCase() === currentUserName.trim().toLowerCase()
          );
          const punctuationMatch = names.find(name =>
            name.replace(/[.,!?;:]/g, '').trim().toLowerCase() ===
            currentUserName.replace(/[.,!?;:]/g, '').trim().toLowerCase()
          );
          
          // Enhanced partial name matching - handle first name + last initial patterns
          let partialMatch;
          if (!exactMatch && !trimmedMatch && !punctuationMatch) {
            const extractFirstAndLast = (fullName: string): { first: string; last: string } => {
              const parts = fullName.replace(/[.,]/g, '').trim().split(/\s+/).filter(p => p.length > 0);
              return {
                first: parts[0]?.toLowerCase() || '',
                last: parts[parts.length - 1]?.toLowerCase() || ''
              };
            };

            const emailNameParts = extractFirstAndLast(currentUserName);
            partialMatch = names.find(employeeName => {
              const employeeNameParts = extractFirstAndLast(employeeName);
              return emailNameParts.first === employeeNameParts.first &&
                     emailNameParts.last === employeeNameParts.last;
            });
          }
          
          const nameToSet = exactMatch || trimmedMatch || punctuationMatch || partialMatch;
          if (nameToSet) {
            setName(nameToSet);
          } else {
            setName('');
          }
        } else {
          setName('');
        }
      } catch (error) {
        setName('');
      }
    } else {
      setName('');
    }
  };

  const handleSubmit = async (keepFormOpen: boolean = false) => {
    if (!date || !name || !type) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await dayAwayService.submitTimeOff({
        date: format(date, 'yyyy-MM-dd'),
        name,
        type,
        note
      });

      toast({
        title: "Success!",
        description: "Time off request has been recorded.",
      });

      if (keepFormOpen) {
        resetForm();
      } else {
        resetForm();
      }
    } catch (error) {
      console.error('Error submitting time off:', error);
      toast({
        title: "Error",
        description: "Failed to submit time off request. Please try again.",
        variant: "destructive"
      });
    }
    setIsSubmitting(false);
  };

  return (
    <div className="p-4">
      <div className="flex-1 space-y-2 p-4 pt-2">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Dashboard</h2>
            <p className="text-gray-500 dark:text-gray-400">
              Submit your time off requests quickly and easily
            </p>
          </div>
          {syncStatus?.lastSync && (
            <div className="text-right">
              <p className="text-xs flex items-center justify-end text-yellow-500 dark:text-yellow-400">
                <Clock className="h-3 w-3 mr-1" />
                Last Sync: {syncStatus.lastSync.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true
                })}
              </p>
            </div>
          )}
        </div>
      </div>

      <MotivationalMessage />

      <div className="max-w-6xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-xl">
              <UserPlus className="h-6 w-6 text-cyan-500" />
              Add Time Off
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              {/* Left Column - Calendar */}
              <div className="w-full lg:flex-1 lg:max-w-md border dark:border-gray-700 rounded-lg p-1 bg-white dark:bg-gray-800 flex items-center">
                <div className="w-full flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const targetDate = new Date(date);
                      targetDate.setHours(0, 0, 0, 0);
                      return targetDate < today || date > new Date(new Date().setFullYear(new Date().getFullYear() + 1));
                    }}
                    className="scale-110 md:scale-125 dark:bg-gray-800 dark:text-gray-100 [&_.rdp-day]:dark:text-gray-100 [&_.rdp-day_button:hover]:dark:!bg-gray-600 [&_.rdp-day_button:hover]:dark:!text-black [&_.rdp-day_button:focus]:dark:!bg-gray-600 [&_.rdp-day_button:focus]:dark:!text-black [&_.rdp-day_button.rdp-day_selected]:dark:bg-cyan-600 [&_.rdp-day_button.rdp-day_selected]:dark:text-gray-100"
                  />
                </div>
              </div>

              {/* Right Column - Form */}
              <div className="w-full lg:flex-1 space-y-6">
                <div className="grid gap-2">
                  <label className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 text-cyan-500" />
                    Selected Date
                  </label>
                  <div className="text-lg font-semibold text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/20 p-3 rounded-lg">
                    {date ? format(date, 'MMMM d, yyyy') : 'No date selected'}
                  </div>
                </div>

                <div className="grid gap-2">
                  <label className="font-medium text-gray-900 dark:text-gray-100">Name *</label>
                  <Select value={name} onValueChange={setName} disabled={true}>
                    <SelectTrigger className="h-12 bg-gray-100 dark:bg-gray-700 cursor-not-allowed">
                      <SelectValue placeholder={isLoading ? "Loading names..." : name || "Your name will be auto-selected"} />
                    </SelectTrigger>
                    <SelectContent>
                      {names
                          .filter(n => n && n.trim() !== '')
                          .map((n) => (
                            <SelectItem key={`name-${n}`} value={n}>{n}</SelectItem>
                          ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <label className="font-medium text-gray-900 dark:text-gray-100">Type *</label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select time off type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Half">Half Day</SelectItem>
                      <SelectItem value="Full">Full Day</SelectItem>
                      <SelectItem value="Email Only">Email Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <label className="font-medium text-gray-900 dark:text-gray-100">Note (Optional)</label>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="min-h-[100px] resize-none"
                    placeholder="Add any additional notes about your time off..."
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t dark:border-gray-700">
                  <Button
                    variant="outline"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    className="px-6"
                  >
                    Clear Form
                  </Button>
                  <Button
                    onClick={() => handleSubmit(true)}
                    disabled={isSubmitting || !date || !name || !type}
                    className="relative px-6 bg-cyan-500 hover:bg-cyan-600"
                  >
                    {isSubmitting && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-200/50 dark:bg-gray-800/50 rounded-md">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-600"></div>
                      </div>
                    )}
                    Submit Time Off
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Tips Card */}
        <Card className="mt-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Quick Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-cyan-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Plan Ahead</p>
                  <p className="text-gray-600 dark:text-gray-400">Submit requests as early as possible for better team coordination.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-cyan-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Be Specific</p>
                  <p className="text-gray-600 dark:text-gray-400">Add notes to help your team understand your time off needs.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-cyan-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Check Calendar</p>
                  <p className="text-gray-600 dark:text-gray-400">Visit the Calendar page to see team availability and plan accordingly.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
